import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchData } from '../../../utils';
import { QueryOptions } from '../../hookTypes';
import {
  PSD2Banks,
  PSD2ClientAccount,
  PSD2ClientConsentInformation,
  PSD2IdentificationPayment,
  PSD2ProcessResponse,
  PSD2SessionStatusResponse,
} from './usePsd2Offline';
import useError from '../../useError';
import useSmartlook from '../../useSmartlook';

export function usePSD2ProxyAccountsQuery(enabled = true) {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useQuery<{
    bankAccounts: PSD2ClientAccount[];
  }>({
    queryKey: ['client', 'psd', 'accounts'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/client/psd/accounts`);

        return resp;
      } catch (e) {
        error('[usePSD2ProxyAccountsQuery][GET /client/psd/accounts]', e);
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyBanksQuery(enabled = true) {
  const { showError } = useError();
  return useQuery<{
    banks: PSD2Banks[];
  }>({
    queryKey: ['psd2', 'proxy', 'banks'],
    queryFn: async () => {
      try {
        const resp = await fetchData('/client/psd/banks');

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyClientConsentsQuery(enabled = true) {
  const { showError } = useError();
  return useQuery<PSD2ClientConsentInformation>({
    queryKey: ['client', 'psd', 'consents', 'search'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/client/psd/consents/search`);

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyInitPaymentMutation() {
  const { showError } = useError();
  return useMutation<
    PSD2IdentificationPayment,
    unknown,
    { clientIban: string }
  >({
    mutationFn: async ({ clientIban }) => {
      try {
        const resp = await fetchData(
          `/client/psd/payment?clientIban=${clientIban}`,
          { method: 'POST' },
        );

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxySessionsDownloadMutation() {
  return useMutation<void, unknown>({
    mutationFn: async () => {
      await fetchData(`/client/psd/sessions/download`, {
        method: 'POST',
      });
    },
  });
}

export function usePSD2ProxySessionsInitMutation() {
  const { showError } = useError();
  return useMutation<
    void,
    unknown,
    {
      bankAccountChange: boolean;
      bankCode: string;
    }
  >({
    mutationFn: async (data) => {
      try {
        await fetchData('/client/psd/sessions/init', {
          method: 'POST',
          body: JSON.stringify({ ...data, psdSource: 'REGISTRATION' }),
        });
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxyConfirmConsentsMutation() {
  const { showError } = useError();
  return useMutation<PSD2ProcessResponse, unknown, { consent: boolean }>({
    mutationFn: async ({ consent }) => {
      try {
        const resp = await fetchData(
          `/client/psd/sessions/process?consent=${consent}`,
          { method: 'POST' },
        );

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxyStatusQuery(queryParams: QueryOptions = {}) {
  const { showError } = useError();
  return useQuery<PSD2SessionStatusResponse>({
    queryKey: ['client', 'psd', 'status'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/client/psd/status`);

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
    refetchInterval: 5000,
    ...queryParams,
  });
}
